import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Moon, Sun, Menu, X, ShoppingCart } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (!user) { setCartCount(0); return; }
    const fetchCart = async () => {
      try {
        const res = await axios.get(`${API}/cart/`);
        setCartCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch { setCartCount(0); }
    };
    fetchCart();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-black/60 border-b border-border/40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Logo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium hover:text-primary transition-colors">Beranda</Link>
            <Link to="/packages" className="text-sm font-medium hover:text-primary transition-colors">Paket Umrah</Link>
            {user && (
              <Link to={user.role === 'client' ? '/dashboard' : '/admin'} className="text-sm font-medium hover:text-primary transition-colors">
                Dashboard
              </Link>
            )}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>

            {user ? (
              <>
                <Link to="/profile" className="text-sm font-medium hover:text-primary transition-colors">
                  {user.role === 'admin' || user.role === 'super_admin' ? 'Admin' : (user.full_name || user.email)}
                </Link>
                <Button variant="ghost" onClick={handleLogout}>Keluar</Button>
                <Link to="/cart">
                  <Button className="rounded-full bg-primary hover:bg-primary/90 relative">
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    Keranjang
                    {cartCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-button">Masuk</Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="register-button">
                    Daftar
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <Link to="/" className="block py-2 text-sm font-medium hover:text-primary transition-colors">Beranda</Link>
            <Link to="/packages" className="block py-2 text-sm font-medium hover:text-primary transition-colors">Paket Umrah</Link>
            {user && (
              <Link to={user.role === 'client' ? '/dashboard' : '/admin'} className="block py-2 text-sm font-medium hover:text-primary transition-colors">
                Dashboard
              </Link>
            )}
            <div className="pt-4 space-y-2">
              {user ? (
                <>
                  <Link to="/cart">
                    <Button className="w-full rounded-full bg-primary hover:bg-primary/90 mb-2 relative">
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      Keranjang
                      {cartCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {cartCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Button variant="outline" className="w-full" onClick={handleLogout}>Keluar</Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="w-full mb-2">Masuk</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="w-full rounded-full bg-primary hover:bg-primary/90">
                      Daftar
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
